<script setup lang="ts">
import OneNetworkConfig from '@/onenetwork/core/onenetwork.config'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { uuid } from 'vue3-uuid'
import {
  useLinkedServiceListStore,
  LinkedServiceListActionTypes,
} from '@/modules/linkedservice/stores/linkedservice.list.store.ts'
import { Icon } from '@iconify/vue'
import { useToast } from 'primevue/usetoast'
import {
  initialValues as _initialValues,
} from '@/modules/linkedservice/helper/linkedservice'
import { SERVICE_TYPE } from '@/modules/linkedservice/constants/linkedservice.const'
import DashboardLayout from '@/onenetwork/layouts/DashboardLayout.vue'
import { schemaLinkedService } from '../helper/schema'
import { yupResolver } from '@primevue/forms/resolvers/yup'
import ImagePicker from '@/onenetwork/components/image_picker/ImagePicker.vue'
import { GoogleMap, Marker } from 'vue3-google-map'

import { useConfirm } from 'primevue'

const toast = useToast()
const confirm = useConfirm()
const router = useRouter()

interface Props {
  id: number
  approveForm?: boolean
}

const linkedServiceListStore = useLinkedServiceListStore()

const props = defineProps<Props>()
const initialValues = ref(_initialValues)

const formRef = ref()

const resolver = yupResolver(schemaLinkedService)

const onFormSubmit = async ({
  values,
  valid,
  errors,
}: {
  values: any
  valid: boolean
  errors: any
}) => {
  if (!valid) {
    return
  }
  // get form values
  confirm.require({
    message: 'Bạn có muốn tạo mới tin đăng này?',
    header: 'Xác nhận',
    // icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: async () => {
      try {
        await linkedServiceListStore.createLinkedService(values)

        initialValues.value = values
        formRef.value?.reset()
        toast.add({
          severity: 'info',
          summary: 'Confirmed',
          detail: 'You have created',
          life: 3000,
        })
        linkedServiceListStore.fetchList()
        router.push({ name: 'liked-services' })
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: e?.message || 'Đã có lỗi xảy ra',
          life: 3000,
        })
        return
      }
    },
    reject: () => {
      // toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    },
  })
}

const loading = computed(() => {
  return linkedServiceListStore?.action_type === LinkedServiceListActionTypes.CREATING
})

const handleSave = (e: Event) => {
  const formEl = formRef.value.$el as HTMLFormElement

  const event = new Event('submit', {
    bubbles: true, // Whether the event will bubble up through the DOM or not
    cancelable: true, // Whether the event may be canceled or not
  })

  formEl.dispatchEvent(event)
}

const handleChangeAvatar = (data: any) => {
  formRef.value?.setFieldValue('logo', data.response.data)
}

// Hàm chuyển đổi tọa độ thành địa chỉ
const getAddressFromCoordinates = async (lat: number, lng: number) => {
  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${OneNetworkConfig.map.apiKey}`
    )
    const data = await response.json()
    if (data.status === 'OK' && data.results.length > 0) {
      formRef.value?.setFieldValue('address', data.results[0].formatted_address)
    }
  } catch (error) {
    console.error('Lỗi khi lấy địa chỉ:', error)
  }
}

function handleMapClick(e) {
  const newPosition = {
    lat: e.latLng.lat(),
    lng: e.latLng.lng(),
  }
  formRef.value?.setFieldValue('lat', newPosition.lat);
  formRef.value?.setFieldValue('lng', newPosition.lng);
  getAddressFromCoordinates(newPosition.lat, newPosition.lng)
}

const handleDragEnd = (e) => {
  const newPosition = {
    lat: e.latLng.lat(),
    lng: e.latLng.lng(),
  }
  formRef.value?.setFieldValue('lat', newPosition.lat);
  formRef.value?.setFieldValue('lng', newPosition.lng);
  getAddressFromCoordinates(newPosition.lat, newPosition.lng)
}

const working_hours = ref<any>({
  start: null,
  end: null,
})

watch(
  () => initialValues?.value,
  (newVal) => {
    if (newVal) {
      working_hours.value.start = newVal.working_hours?.start ? new Date(newVal.working_hours.start) : null
      working_hours.value.end = newVal.working_hours?.end ? new Date(newVal.working_hours.end) : null
    }
  },
  { immediate: true, deep: true },
)

watch(
  working_hours,
  (newVal) => {
    formRef.value?.setFieldValue('working_hours', {
      start: newVal.start ? newVal.start.toISOString() : null,
      end: newVal.end ? newVal.end.toISOString() : null,
    })
  },
  { immediate: true, deep: true },
)

const mapRef = ref<GoogleMap | null>(null)
const inputAutuocomplete = ref<HTMLInputElement>(null)

watch(() => mapRef.value?.ready, (ready) => {
  if (!ready && inputAutuocomplete.value) return

  inputAutuocomplete.value = new google.maps.places.Autocomplete(
    document.querySelector(
      `input[name="address"]`
    ) as HTMLInputElement,
    {
      types: ['geocode'],
      componentRestrictions: { country: 'vn' },
    }
  )
  
  console.log(inputAutuocomplete.value, 'inputAutuocomplete.value')
  inputAutuocomplete.value.addListener('place_changed', () => {
    const place = inputAutuocomplete.value.getPlace();
    if (place.geometry) {
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      formRef.value?.setFieldValue('lat', lat)
      formRef.value?.setFieldValue('lng', lng)
      formRef.value?.setFieldValue('address', place.formatted_address)
      console.log('place', place)
      mapRef.value.map.setCenter(place.geometry.location)
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Không tìm thấy địa chỉ',
        life: 3000,
      })
    }
  })
})

</script>

<template>
  <DashboardLayout currentRoute="services/create" page-title="Dịch vụ liên kết • Tạo mới">
    <div class="">
      <Form
        ref="formRef"
        v-slot="$form"
        :initialValues
        :resolver
        @submit="onFormSubmit"
      >
        <div class="flex justify-end gap-4 mb-5">
          <div class="flex gap-2">
            <button class="btn btn-success flex items-center gap-3" @click="handleSave">
              <Icon icon="ri:save-3-fill" width="16px" height="16px" /> Lưu
              <Icon icon="eos-icons:three-dots-loading" width="24" height="24" v-if="loading" />
            </button>
          </div>
        </div>
        <div>
          <Card>
            <template #title>
              <div class="flex justify-between">
                <div>Thông tin dịch vụ</div>
              </div>
            </template>
            <template #content>
              <div class="grid grid-cols-2 pt-3">
                <div class="">
                  <div class="flex w-full">
                    <div class="flex flex-1">
                      <image-picker
                        type="photo"
                        :uuid="uuid.v4().toString()"
                        folder="common"
                        height="150px"
                        :upload="true"
                        @action:upload-success="handleChangeAvatar"
                      />
                    </div>
                    <InputText name="logo" hidden type="text" class="form-control" />
                    <div class="flex-3">
                      <div class="flex mb-3 gap-3">
                        <div class="flex-1 text-right">Loại dịch vụ:</div>
                        <div class="flex-2">
                          <Select
                            name="type"
                            :options="SERVICE_TYPE.map((item: any) => ({ name: item.label, code: item.value }))"
                            optionLabel="name"
                            optionValue="code"
                            placeholder="Chọn loại dịch vụ"
                            class="form-control"
                          />
                          <span class="italic text-[12px] text-red-500" v-if="$form.type?.invalid">
                            {{ $form.type.error.message }}
                          </span>
                        </div>
                      </div>
                      <div class="flex mb-3 gap-3">
                        <div class="flex-1 text-right">Tên dịch vụ:</div>
                        <div class="flex-2">
                          <InputText name="name" type="text" class="form-control" placeholder="Nhập tên dịch vụ" />
                          <span class="italic text-[12px] text-red-500" v-if="$form.name?.invalid">
                            {{ $form.name.error.message }}
                          </span>
                        </div>
                      </div>
                      <div class="flex mb-3 gap-3">
                        <div class="flex-1 text-right">Người đại diện:</div>
                        <div class="flex-2">
                          <InputText name="representative_name" type="text" class="form-control" placeholder="Nhập tên người đại diện" />
                          <span class="italic text-[12px] text-red-500" v-if="$form.representative_name?.invalid">
                            {{ $form.representative_name.error.message }}
                          </span>
                        </div>
                      </div>
                      <div class="flex mb-3 gap-3">
                        <div class="flex-1 text-right">Số điện thoại:</div>
                        <div class="flex-2">
                          <InputText name="phone" type="text" class="form-control" placeholder="Nhập số điện thoại" />
                          <span class="italic text-[12px] text-red-500" v-if="$form.phone?.invalid">
                            {{ $form.phone.error.message }}
                          </span>
                        </div>
                      </div>
                      <div class="flex mb-3 gap-3">
                        <div class="flex-1 text-right">Email:</div>
                        <div class="flex-2">
                          <InputText name="email" type="text" class="form-control" placeholder="Nhập email" />
                          <span class="italic text-[12px] text-red-500" v-if="$form.email?.invalid">
                            {{ $form.email.error.message }}
                          </span>
                        </div>
                      </div>
                      <div class="flex mb-3 gap-3">
                        <div class="flex-1 text-right">Mô tả:</div>
                        <div class="flex-2">
                          <InputText name="description" type="text" class="form-control" placeholder="Nhập mô tả" />
                          <span class="italic text-[12px] text-red-500" v-if="$form.description?.invalid">
                            {{ $form.description.error.message }}
                          </span>
                        </div>
                      </div>
                      <div class="flex mb-3 gap-3">
                      <div class="flex-1 text-right">Giờ làm việc:</div>
                      <div class="flex-2">
                        <InputText name="working_hours" hidden />
                        <div class="flex items-center gap-1">
                          <DatePicker
                            v-model="working_hours.start"
                            timeOnly
                            class="form-control"
                            manual-input
                          />
                          <span>~</span>
                          <DatePicker
                            v-model="working_hours.end"
                            timeOnly
                            class="form-control"
                            manual-input
                          />
                        </div>
                        <span class="italic text-[12px] text-red-500" v-if="$form.activation_status?.invalid">
                          {{ $form.activation_status.error.message }}
                        </span>
                      </div>
                    </div>

                    </div>
                  </div>
                </div>

                <div class="flex mb-3 gap-3">
                  <div class="flex-1 text-right">Địa chỉ:</div>
                  <div class="flex-2 space-y-2">
                    <!-- google autocomplete address -->
                    <InputText
                      ref="inputAutuocomplete"
                      name="address"
                      type="text"
                      class="form-control"
                      maxlength="1000"
                      placeholder="Nhập địa chỉ"
                    />
                    <span class="italic text-[12px] text-red-500" v-if="$form.address?.invalid">
                      {{ $form.address.error.message }}
                    </span>
                    <InputText name="lat" type="text" hidden />
                    <InputText name="lng" type="text" hidden />
                    <div class="w-full h-[300px] rounded">
                      <GoogleMap
                        ref="mapRef"
                        :api-key="OneNetworkConfig.map.apiKey"
                        :center="{
                          lat: Number($form.lat?.value),
                          lng: Number($form.lng?.value),
                        }"
                        :zoom="16"
                        style="width: 100%; height: 100%"
                        @click="handleMapClick"
                      >
                        <Marker
                          :options="{
                            position: {
                              lat: Number($form.lat?.value),
                              lng: Number($form.lng?.value),
                            },
                            draggable: true,
                          }"
                          @dragend="handleDragEnd"
                        />
                      </GoogleMap>
                    </div>
                    <div class="text-gray-700 text-xs">
                      Di chuyển marker để lấy vị trí chính xác
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </Card>
        </div>
      </Form>
    </div>
  </DashboardLayout>
</template>

<style scoped></style>
